import React, { useMemo, useState } from 'react';
import { Box, Text, Group, Stack, useMantineColorScheme, TextInput, Textarea, Select, Button, Card, ActionIcon, Tooltip, Switch, Paper, rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconSend, IconBroadcast, IconSparkles, IconUser, IconUsers, IconSettings, IconCheck } from '@tabler/icons-react';
import { notificationService } from '../../services/notificationService';
import { authService } from '../../services/authService';
import { userService, type UserMinDTO } from '../../services/userService';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';

const tipos = [
  { value: 'SISTEMA', label: 'Sistema', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#667eea', icon: IconSettings },
  { value: 'FORO', label: 'Foro', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#4facfe', icon: IconUsers },
  { value: 'EXAMEN', label: 'Examen', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#43e97b', icon: IconSparkles },
  { value: 'PAGO', label: 'Pago', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: '#fa709a', icon: IconSend },
  { value: 'ALERTA', label: 'Alerta', gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', color: '#ff9a9e', icon: IconBell },
];

const AdminNotificationsPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  const currentUser = authService.getCurrentUserFromStorage();

  const [destinatarioId, setDestinatarioId] = useState<number | ''>('');
  const [destinatarioLabel, setDestinatarioLabel] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [tipo, setTipo] = useState<string>('SISTEMA');
  const [metadataRaw, setMetadataRaw] = useState<string>('');
  const [anuncioGeneral, setAnuncioGeneral] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);

  // Buscar usuarios por texto (email/username/nombre)
  const fetchUsers = async (q: string) => {
    if (!q || q.trim().length < 2) {
      setOptions([]);
      return;
    }
    try {
      setSearchLoading(true);
      const users: UserMinDTO[] = await userService.searchUsers(q.trim(), 0, 8);
      const opts = users.map(u => ({
        value: String(u.id),
        label: `${u.nombre || ''} ${u.apellidos || ''} — ${u.username} <${u.email}>`.trim(),
      }));
      setOptions(opts);
    } catch (e) {
      console.error('Error buscando usuarios:', e);
    } finally {
      setSearchLoading(false);
    }
  };

  const metadata = useMemo(() => {
    if (!metadataRaw.trim()) return undefined;
    try {
      return JSON.parse(metadataRaw);
    } catch {
      return undefined;
    }
  }, [metadataRaw]);

  const handleCrear = async () => {
    if (!titulo.trim() || !mensaje.trim() || !tipo) {
      notifications.show({ color: 'red', title: 'Campos requeridos', message: 'Completa título, mensaje y tipo.' });
      return;
    }
    if (!anuncioGeneral && !destinatarioId) {
      notifications.show({ color: 'red', title: 'Destinatario requerido', message: 'Selecciona un destinatario o activa Anuncio general.' });
      return;
    }
    // Validar JSON si se ingresó
    if (metadataRaw.trim()) {
      try {
        JSON.parse(metadataRaw);
      } catch (e) {
        notifications.show({ color: 'red', title: 'Metadata inválida', message: 'Proporciona un JSON válido en metadata.' });
        return;
      }
    }

    try {
      setSubmitting(true);
      if (anuncioGeneral) {
        const res = await notificationService.broadcast({
          titulo: titulo.trim(),
          mensaje: mensaje.trim(),
          tipo: tipo as any,
          metadata,
        });
        notifications.show({ color: 'green', title: 'Anuncio enviado', message: `Notificaciones creadas: ${res.created}` });
      } else {
        await notificationService.create({
          destinatarioId: Number(destinatarioId),
          titulo: titulo.trim(),
          mensaje: mensaje.trim(),
          tipo: tipo as any,
          metadata,
        });
        notifications.show({ color: 'green', title: 'Notificación enviada', message: 'Se creó y envió la notificación.' });
      }
      setTitulo('');
      setMensaje('');
      setMetadataRaw('');
    } catch (e: any) {
      notifications.show({ color: 'red', title: 'Error al crear', message: e?.response?.data?.message || 'No se pudo crear la notificación.' });
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageTransition type="medical" duration={800}>
    <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
      <Box mb="lg">
        <AdminPageHeader
          title="Centro de Notificaciones"
          subtitle="Crea y gestiona notificaciones para usuarios individuales o anuncios generales"
        />
      </Box>

      {/* Formulario de creación */}
      <Card withBorder radius="lg" p="xl" style={cardStyle}>
        <Stack gap="xl">
          {/* Toggle anuncio general con mejor diseño */}
          <Group align="center" justify="space-between" mb="md">
            <Group align="center" gap="md">
              <ActionIcon
                size="lg"
                variant="subtle"
                style={{
                  background: anuncioGeneral
                    ? textColor
                    : (dark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.08)'),
                  color: anuncioGeneral ? (dark ? '#1e293b' : '#f7f3ee') : mutedColor,
                  borderRadius: rem(12),
                  transition: 'all 0.3s ease',
                }}
              >
                {anuncioGeneral ? <IconUsers size={20} /> : <IconUser size={20} />}
              </ActionIcon>
              <Stack gap={2}>
                <Text fw={600} style={{ color: textColor }}>
                  {anuncioGeneral ? 'Anuncio General' : 'Notificación Individual'}
                </Text>
                <Text size="sm" style={{ color: mutedColor }}>
                  {anuncioGeneral ? 'Enviar a todos los usuarios' : 'Seleccionar destinatario específico'}
                </Text>
              </Stack>
            </Group>
            <Switch
              size="lg"
              checked={anuncioGeneral}
              onChange={(e) => setAnuncioGeneral(e.currentTarget.checked)}
              styles={{
                track: {
                  backgroundColor: anuncioGeneral ? textColor : (dark ? '#374151' : '#d1d5db'),
                  borderColor: 'transparent',
                  transition: 'all 0.3s ease',
                },
                thumb: {
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                }
              }}
            />
          </Group>

          <Group align="flex-start" grow>
            {/* Columna izquierda */}
            <Stack gap="md" style={{ flex: 1 }}>
              {!anuncioGeneral && (
                <Select
                  label="Destinatario"
                  placeholder="Buscar por email, username o nombre..."
                  description="Busca y selecciona el usuario que recibirá la notificación"
                  searchable
                  nothingFoundMessage={searchLoading ? 'Buscando...' : 'Sin resultados'}
                  data={options}
                  searchValue={searchValue}
                  onSearchChange={(val) => {
                    setSearchValue(val);
                    clearTimeout((window as any).__uSearchTimer);
                    (window as any).__uSearchTimer = setTimeout(() => fetchUsers(val), 300);
                  }}
                  value={destinatarioId ? String(destinatarioId) : null}
                  onChange={(val) => {
                    const opt = options.find(o => o.value === val);
                    setDestinatarioId(val ? Number(val) : '');
                    setDestinatarioLabel(opt?.label || '');
                  }}
                  styles={{
                    input: {
                      borderRadius: rem(12),
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                      '&:focus': {
                        borderColor: '#8b5cf6',
                        boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)',
                      }
                    }
                  }}
                />
              )}

              <Select
                label="Tipo de notificación"
                description="Selecciona la categoría que mejor describa esta notificación"
                data={tipos.map(t => ({ value: t.value, label: t.label }))}
                value={tipo}
                onChange={(val) => setTipo(val || 'SISTEMA')}
                styles={{
                  input: {
                    borderRadius: rem(12),
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                    '&:focus': {
                      borderColor: tipos.find(t => t.value === tipo)?.color || '#8b5cf6',
                      boxShadow: `0 0 0 2px ${tipos.find(t => t.value === tipo)?.color || '#8b5cf6'}20`,
                    }
                  }
                }}
              />

              {!anuncioGeneral && destinatarioId && (
                <Paper
                  p="md"
                  style={{
                    background: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)'}`,
                    borderRadius: rem(12),
                  }}
                >
                  <Group gap={6} align="center">
                    <IconCheck size={15} color="#22c55e" />
                    <Text size="sm" fw={500} style={{ color: '#22c55e' }}>
                      Destinatario seleccionado
                    </Text>
                  </Group>
                  <Text size="xs" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                    {destinatarioLabel}
                  </Text>
                </Paper>
              )}
            </Stack>

            {/* Columna derecha */}
            <Stack gap="md" style={{ flex: 1 }}>
              <TextInput
                label="Título"
                placeholder="Ej: Nueva actualización disponible"
                description="Un título claro y conciso para la notificación"
                value={titulo}
                onChange={(e) => setTitulo(e.currentTarget.value)}
                styles={{
                  input: {
                    borderRadius: rem(12),
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                    '&:focus': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)',
                    }
                  }
                }}
              />

              <Textarea
                label="Mensaje"
                placeholder="Describe el contenido de la notificación de manera clara y útil..."
                description="El contenido principal que verá el usuario"
                minRows={4}
                value={mensaje}
                onChange={(e) => setMensaje(e.currentTarget.value)}
                styles={{
                  input: {
                    borderRadius: rem(12),
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                    '&:focus': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)',
                    }
                  }
                }}
              />

              <Textarea
                label="Metadata (Opcional)"
                placeholder='{"examenId": 123, "ruta": "/examenes/123", "prioridad": "alta"}'
                description="Datos adicionales en formato JSON para funcionalidades avanzadas"
                minRows={3}
                value={metadataRaw}
                onChange={(e) => setMetadataRaw(e.currentTarget.value)}
                styles={{
                  input: {
                    borderRadius: rem(12),
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: rem(13),
                    '&:focus': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)',
                    }
                  }
                }}
              />
            </Stack>
          </Group>

          <Group justify="flex-end" mt="md">
            <Button
              size="md"
              radius="md"
              loading={submitting}
              onClick={handleCrear}
              leftSection={anuncioGeneral ? <IconBroadcast size={18} /> : <IconSend size={18} />}
              styles={primaryBtn}
            >
              {anuncioGeneral ? 'Enviar Anuncio General' : 'Crear Notificación'}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Leyenda de tipos */}
      <Card withBorder radius="lg" p="xl" mt="lg" style={cardStyle}>
        <Group align="center" mb="lg" gap="xs">
          <IconSparkles size={18} color={mutedColor} />
          <Text fw={600} size="lg" style={{ color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
            Tipos de Notificación
          </Text>
        </Group>

        <Group gap="md">
          {tipos.map(({ value, label, gradient, color, icon: Icon }) => (
            <Tooltip key={value} label={`Tipo: ${label}`}>
              <Paper
                p="sm"
                style={{
                  background: colorScheme === 'dark'
                    ? `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`
                    : `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  minWidth: 120,
                  textAlign: 'center',
                  transform: tipo === value ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: tipo === value
                    ? `0 8px 25px -8px ${color}50, 0 0 0 2px ${color}30`
                    : 'none',
                  border: tipo === value
                    ? `2px solid ${color}`
                    : `1px solid ${color}30`,
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: `0 12px 32px -8px ${color}40`,
                    border: `1px solid ${color}60`,
                  }
                }}
                onClick={() => setTipo(value)}
              >
                <Group gap="xs" justify="center" align="center">
                  <Icon size={16} style={{ color }} />
                  <Text size="sm" fw={500} style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
                    {label}
                  </Text>
                </Group>
              </Paper>
            </Tooltip>
          ))}
        </Group>

        <Text size="xs" mt="md" style={{ color: mutedColor, textAlign: 'center' }}>
          Haz clic en cualquier tipo para seleccionarlo automáticamente
        </Text>
      </Card>
    </Box>
    </PageTransition>
  );
};

export default AdminNotificationsPage;
